import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { useBranding } from "@/context/BrandingContext";
import { findEnabledSection, sectionFlag } from "@/lib/useTemplateSections";
import { resolveButtonStyle } from "@/lib/ctaButtonStyle";

// Repli agence — utilisé seulement si l'API échoue (loaded=true, sections=null)
// pour garder la page lisible. JAMAIS pendant le chargement initial afin
// d'éviter le flash du contenu agence avant la vraie nav de la boutique.
const NAV_FALLBACK = [
  { label: "Offres", to: "/boutique" },
  { label: "Processus", to: "/#processus" },
  { label: "Tarifs", to: "/#tarifs" },
  { label: "Contact", to: "/#contact" },
];
const DEFAULT_CTA = { label: "Démarrer", url: "#contact" };

// `loaded` (second élément du tuple retourné par useTemplateSections, transmis
// par Layout.jsx) vaut false tant que l'API n'a pas encore répondu. On retourne
// alors [] / null pour éviter le flash du contenu agence. Si l'API échoue
// (loaded=true mais sections=null), on bascule sur le repli d'agence.
function resolveNav(sections, loaded) {
  if (!loaded) return [];
  if (sections === null) return NAV_FALLBACK;
  const menuSection = findEnabledSection(sections, "header_menu");
  const items = (menuSection?.blocks || []).filter((b) => b.enabled !== false && b.props?.label && b.props?.url);
  return items.map((b) => ({ label: b.props.label, to: b.props.url }));
}

function resolveCta(sections, loaded) {
  if (!loaded) return null;
  if (sections === null) return { ...DEFAULT_CTA, style: resolveButtonStyle({}, "cta") };
  const menuSection = findEnabledSection(sections, "header_menu");
  if (!menuSection) return null;
  const label = menuSection.props?.cta_label;
  if (!label) return null;
  return {
    label,
    url: menuSection.props?.cta_url || DEFAULT_CTA.url,
    style: resolveButtonStyle(menuSection.props, "cta"),
  };
}

// Exporté pour Layout.jsx : l'en-tête est FIXE, la page réserve sa hauteur
// en padding — et le style "stacked" (deux rangées sur desktop) est plus
// haut que les autres (voir STACKED_DESKTOP_HEIGHT).
export function resolveHeaderLayout(sections, loaded) {
  if (!loaded || sections === null) return "classic";
  const menuSection = findEnabledSection(sections, "header_menu");
  const v = menuSection?.props?.header_layout;
  return ["classic", "centered", "minimal", "stacked"].includes(v) ? v : "classic";
}

// Hauteur desktop du style "stacked" : rangée logo (72px) + rangée menu (48px).
export const STACKED_DESKTOP_HEIGHT = 120;

export const Header = ({ onSearch, sections, loaded = true, offsetTop = true, transparent = false }) => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const branding = useBranding();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  // En-tête transparent au-dessus du bandeau : opaque dès qu'on défile.
  useEffect(() => {
    if (!transparent) return undefined;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);
  const isTransparent = transparent && !scrolled;
  const NAV_ITEMS = resolveNav(sections, loaded);
  const cta = resolveCta(sections, loaded);
  const showSearch = sectionFlag(sections, "header_menu", "show_search", true, loaded);
  const showCart = sectionFlag(sections, "header_menu", "show_cart", true, loaded);
  // Lien « Se connecter » / « Mon espace » (en-tête ET menu déroulant mobile).
  const showAccount = sectionFlag(sections, "header_menu", "show_account", true, loaded);
  const layout = resolveHeaderLayout(sections, loaded);

  // Logo : centré en absolu sur mobile (entre le bouton menu et les actions)
  // pour tous les styles ; sur desktop il revient dans le flux. Pour le
  // style "centered", il est centré par la mise en page à trois colonnes
  // ci-dessous (menu | logo | actions) et NON en absolu : positionné en
  // absolu, il passait par-dessus le menu de gauche et le recouvrait dès que
  // l'en-tête était un peu étroit (aperçu du Studio) ou le menu un peu long.
  const isCentered = layout === "centered";
  // "stacked" : sur desktop, le logo est seul au centre de la première
  // rangée (entre une colonne vide et les actions, de même largeur), et le
  // menu + le bouton d'appel occupent une seconde rangée centrée, sous un
  // filet. Sur mobile, rien ne change (une seule barre, burger + tiroir).
  const isStacked = layout === "stacked";
  const logoClass = `absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0${isCentered || isStacked ? " md:mx-6" : ""}`;
  // Position du menu sur desktop selon le style. Marges LOGIQUES (ms/me) et
  // non physiques (ml/mr) : identiques en français, mais correctement
  // reflétées quand le module multilingue passe la page en arabe
  // (dir="rtl") — une marge physique y décollerait le menu du mauvais côté.
  // Les libellés ne sont jamais coupés au milieu (whitespace-nowrap) ; si la
  // place manque, c'est le menu entier qui se replie sur deux lignes
  // (flex-wrap) au lieu de déborder de l'en-tête.
  // "centered" : menu et actions prennent chacun une colonne de même largeur
  // (flex-1) de part et d'autre du logo, qui reste ainsi au centre. Le menu
  // passe AVANT le logo (order-first) : dans le DOM il vient après, pour que
  // sur mobile le logo reste entre le bouton menu et les actions.
  const navPosClass =
    layout === "minimal"
      ? "md:ms-auto md:me-5 md:gap-x-6"
      : isCentered
        ? "md:order-first md:flex-1 md:justify-start md:gap-x-6 lg:gap-x-8"
        : "md:gap-x-8";
  const actionsPosClass = isCentered || isStacked ? "md:flex-1 md:justify-end" : "";

  // Couleurs adaptées au mode transparent (texte clair sur le bandeau).
  const linkClass = isTransparent
    ? "text-white/85 hover:text-white"
    : "text-header-muted hover:text-header-text";

  const navLinks = NAV_ITEMS.map((n) =>
    n.to.includes("#") ? (
      <a
        key={n.to}
        href={n.to}
        data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`text-[14px] font-medium whitespace-nowrap transition-colors ${linkClass}`}
      >
        {n.label}
      </a>
    ) : (
      <Link
        key={n.to}
        to={n.to}
        data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`text-[14px] font-medium whitespace-nowrap transition-colors ${linkClass}`}
      >
        {n.label}
      </Link>
    )
  );

  return (
    <>
    <header
      data-testid="site-header"
      data-header-layout={layout}
      data-transparent={isTransparent ? "true" : "false"}
      className={`fixed ${offsetTop ? "top-9" : "top-0"} z-40 w-full transition-[background-color,border-color,backdrop-filter] duration-300 ${
        isTransparent
          ? "bg-transparent border-b border-transparent"
          : "backdrop-blur-xl bg-header-bg/80 border-b border-stone/70"
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-[72px] px-5 md:px-10 max-w-[1240px] mx-auto">
        <button
          data-testid="mobile-menu-toggle"
          className={`md:hidden relative z-10 transition-colors ${isTransparent ? "text-white" : "text-header-text"}`}
          onClick={() => setMenuOpen(true)}
          aria-label="Menu"
        >
          <Menu className="h-6 w-6" strokeWidth={1.6} />
        </button>

        <Link
          to="/"
          data-testid="logo-link"
          aria-label={`${branding.name} — accueil`}
          className={`select-none ${logoClass} inline-flex items-center hover:opacity-90 transition-opacity`}
        >
          <Logo to={null} testId="logo-inner" size="md" variant={isTransparent ? "light" : "dark"} showBrandingTagline={false} />
        </Link>

        {isStacked ? (
          // Colonne vide, symétrique des actions : garde le logo au centre.
          <div className="hidden md:block md:flex-1 md:order-first" aria-hidden="true" />
        ) : (
          <nav className={`hidden md:flex md:flex-wrap items-center gap-y-1 ${navPosClass} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}>
            {navLinks}
          </nav>
        )}

        <div className={`flex items-center gap-3 md:gap-4 relative z-10 ${actionsPosClass}`}>
          {/* Module multilingue : invisible tant que l'admin ne l'a pas
              activé sur son thème — en-tête inchangé partout ailleurs. */}
          <LanguageSwitcher linkClass={linkClass} />
          {showSearch && (
            <button
              data-testid="open-search-btn"
              onClick={onSearch}
              className={`transition-colors ${linkClass}`}
              aria-label="Rechercher"
            >
              <Search className="h-[18px] w-[18px]" strokeWidth={1.8} />
            </button>
          )}
          {showCart && (
            <button
              data-testid="open-cart-btn"
              onClick={() => setOpen(true)}
              className={`relative transition-colors ${linkClass}`}
              aria-label="Panier"
            >
              <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.8} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 bg-gold text-white text-[10px] font-mono h-4 w-4 flex items-center justify-center rounded-full"
                >
                  {count}
                </span>
              )}
            </button>
          )}
          {showAccount && (
            <button
              data-testid="account-link"
              onClick={() => navigate(user ? (user.role === "admin" || user.role === "superadmin" ? "/admin" : "/compte") : "/connexion")}
              className={`hidden md:inline-flex text-[14px] font-medium transition-colors items-center gap-1.5 ${linkClass}`}
            >
              <User className="h-[18px] w-[18px]" strokeWidth={1.8} />
              <span className="hidden lg:inline">{user ? "Mon espace" : "Se connecter"}</span>
            </button>
          )}
          {cta && (
            <a
              href={cta.url}
              data-testid="header-quote-cta"
              className={`hidden sm:inline-flex ${isStacked ? "md:hidden " : ""}items-center gap-1.5 bg-ink text-white px-5 py-2.5 text-[13px] font-semibold hover:opacity-90 transition-opacity animate-fade-in ${cta.style.className}`}
              style={cta.style.style}
              aria-label={!cta.style.showText ? cta.label : undefined}
            >
              {cta.style.showIcon && cta.style.Icon && <cta.style.Icon className="h-3.5 w-3.5" />}
              {cta.style.showText && cta.label}
            </a>
          )}
        </div>
      </div>

      {isStacked && (NAV_ITEMS.length > 0 || cta) && (
        <div className={`hidden md:block border-t ${isTransparent ? "border-white/20" : "border-stone/70"}`}>
          <nav
            data-testid="stacked-nav"
            className={`flex flex-wrap items-center justify-center gap-x-8 gap-y-1 h-12 px-10 max-w-[1240px] mx-auto transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
          >
            {navLinks}
            {cta && (
              <a
                href={cta.url}
                data-testid="stacked-quote-cta"
                className={`inline-flex items-center gap-1.5 bg-ink text-white px-5 py-2 text-[13px] font-semibold hover:opacity-90 transition-opacity ${cta.style.className}`}
                style={cta.style.style}
                aria-label={!cta.style.showText ? cta.label : undefined}
              >
                {cta.style.showIcon && cta.style.Icon && <cta.style.Icon className="h-3.5 w-3.5" />}
                {cta.style.showText && cta.label}
              </a>
            )}
          </nav>
        </div>
      )}
    </header>

    {menuOpen && (
      <div className="fixed inset-0 z-[60] md:hidden">
        <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
        {/* Le tiroir de menu mobile prend le fond de l'EN-TÊTE et non celui de
            la page : son contenu utilise déjà les jetons de texte d'en-tête,
            et les dissocier donnerait du texte clair sur fond clair dès qu'une
            boutique choisit un en-tête sombre. */}
        <div className="absolute left-0 top-0 h-full w-[82%] max-w-xs bg-header-bg p-6 animate-fade-in flex flex-col shadow-2xl">
          <div className="flex items-center justify-between mb-8">
            <Logo to={null} size="sm" variant="dark" testId="mobile-menu-logo" showBrandingTagline={false} />
            <button
              data-testid="close-mobile-menu"
              onClick={() => setMenuOpen(false)}
              aria-label="Fermer le menu"
              className="text-header-text p-1 -mr-1"
            >
              <X className="h-6 w-6" strokeWidth={1.6} />
            </button>
          </div>
          <nav className="flex flex-col gap-5 flex-1 overflow-y-auto">
            {NAV_ITEMS.map((n) =>
              n.to.includes("#") ? (
                <a
                  key={n.to}
                  href={n.to}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`drawer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display font-semibold text-2xl text-header-text hover:text-gold transition-colors"
                >
                  {n.label}
                </a>
              ) : (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`drawer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display font-semibold text-2xl text-header-text hover:text-gold transition-colors"
                >
                  {n.label}
                </Link>
              )
            )}
            {cta && (
              <a
                href={cta.url}
                onClick={() => setMenuOpen(false)}
                data-testid="drawer-quote-cta"
                className={`inline-flex items-center justify-center gap-2 bg-ink text-white text-center py-3.5 text-sm font-semibold mt-2 ${cta.style.className}`}
                style={cta.style.style}
                aria-label={!cta.style.showText ? cta.label : undefined}
              >
                {cta.style.showIcon && cta.style.Icon && <cta.style.Icon className="h-4 w-4" />}
                {cta.style.showText && cta.label}
              </a>
            )}
            {showAccount && (
              <>
                <div className="h-px bg-stone/70 my-2" />
                <Link
                  to={user ? (user.role === "admin" || user.role === "superadmin" ? "/admin" : "/compte") : "/connexion"}
                  onClick={() => setMenuOpen(false)}
                  data-testid="drawer-account-link"
                  className="font-display font-semibold text-2xl text-header-text hover:text-gold transition-colors"
                >
                  {user ? (user.role === "admin" || user.role === "superadmin" ? "Administration" : "Mon compte") : "Se connecter"}
                </Link>
              </>
            )}
          </nav>
        </div>
      </div>
    )}
    </>
  );
};
